import { useEffect, useRef } from 'react'
import { StudyModel } from '@common/config/api/client'
import type { PHIContainer, SelectedSeries } from '../../../types/phi'

// Define types for timepoint submission data
type ContainerSubmission = {
  container_submission_id: string
  dicom_json?: {
    studies: StudyModel[]
  } | null
  [key: string]: unknown
}

type TimePointSubmissionData = {
  data?: {
    timepoint_submission: {
      container_submissions: ContainerSubmission[]
      [key: string]: unknown
    }
    [key: string]: unknown
  }
  [key: string]: unknown
}

interface UseDataProcessingProps {
  timePointSubmissionData: TimePointSubmissionData | undefined
  isTimePointSubmissionLoading: boolean
  phiData: { containers?: PHIContainer[] } | undefined
  selectedSeries: SelectedSeries
  onSetDataToRender: (data: Record<string, StudyModel>) => void
  onSetSelectedSeries: (series: SelectedSeries) => void
}

export function useDataProcessing({
  timePointSubmissionData,
  isTimePointSubmissionLoading,
  phiData,
  selectedSeries,
  onSetDataToRender,
  onSetSelectedSeries,
}: UseDataProcessingProps) {
  // Use ref to track if we've already initialized the default series
  const hasInitializedSeriesRef = useRef(false)

  // Process timepoint submission data when it loads
  useEffect(() => {
    if (timePointSubmissionData && !isTimePointSubmissionLoading) {
      const result: StudyModel[] = []

      // Extract studies from container submissions
      timePointSubmissionData.data?.timepoint_submission.container_submissions.forEach((item) => {
        if (item.dicom_json) {
          result.push(...(item.dicom_json.studies as StudyModel[]))
        }
      })

      // Prepare data by combining series from same study
      const preparedData = result.reduce(
        (acc, item) => {
          if (acc[item.StudyInstanceUID]) {
            acc[item.StudyInstanceUID].series.push(...item.series)
          } else {
            acc[item.StudyInstanceUID] = item
          }

          return acc
        },
        {} as Record<string, StudyModel>,
      )

      // Get all studies instead of just the first one
      const allStudies = Object.keys(preparedData)

      // Only set default series if we haven't initialized one yet AND no series is selected
      if (allStudies.length > 0 && !hasInitializedSeriesRef.current && !selectedSeries.series) {
        const firstStudy = allStudies[0]
        if (preparedData[firstStudy].series.length > 0) {
          const firstSeriesInstanceUID = preparedData[firstStudy].series[0].SeriesInstanceUID

          // Get metadata IDs from PHI data
          let examMetadataId = ''
          let seriesMetadataId = ''

          if (phiData?.containers) {
            for (const container of phiData.containers) {
              const matchingExam = container.exams.find((exam) => exam.study_instance_uid === firstStudy)

              if (matchingExam) {
                const matchingSeries = matchingExam.series.find(
                  (series) => series.series_instance_uid === firstSeriesInstanceUID,
                )

                if (matchingSeries) {
                  examMetadataId = matchingExam.exam_metadata_id
                  seriesMetadataId = matchingSeries.series_metadata_id
                  break
                }
              }
            }
          }

          onSetSelectedSeries({
            series: firstSeriesInstanceUID,
            study: firstStudy,
            exam_metadata_id: examMetadataId,
            series_metadata_id: seriesMetadataId,
          })

          hasInitializedSeriesRef.current = true
        }
      }

      onSetDataToRender(preparedData)
    }
    // selectedSeries.series is excluded: it is set inside this effect, adding it causes
    // an infinite loop (effect → setSelectedSeries → re-render → effect again).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTimePointSubmissionLoading,
    timePointSubmissionData,
    phiData,
    onSetDataToRender,
    onSetSelectedSeries,
    // Removed selectedSeries.series from dependencies to prevent infinite loop
  ])

  // Helper function to get series to process
  const getSeriesForStudy = (studyUID: string, dataToRender: Record<string, StudyModel>) => {
    return dataToRender[studyUID]?.series || []
  }

  // Helper function to get current series to process
  const getCurrentSeries = (dataToRender: Record<string, StudyModel>) => {
    if (!selectedSeries.study || !selectedSeries.series) return null

    return dataToRender[selectedSeries.study]?.series.find((x) => x.SeriesInstanceUID === selectedSeries.series) || null
  }

  return {
    getSeriesForStudy,
    getCurrentSeries,
  }
}
